import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, COUNTRY_FLAGS, COUNTRY_LABELS, CURRENCY_SYMBOLS } from "@/lib/api";
import {
  BadgeCheck, Building2, Rocket, BarChart3, MessageCircle, Bell, Users,
  ArrowRight, Check, Sparkles, HelpCircle, TrendingUp, Star,
} from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
  { icon: BadgeCheck, color: "#11783A", title: "Badge Pro vérifié", desc: "Un tampon d'authenticité visible sur toutes vos annonces, gage de sérieux pour vos futurs clients." },
  { icon: Building2, color: "#D6202C", title: "Vitrine d'agence dédiée", desc: "Une page publique /agence/votre-nom qui regroupe toutes vos annonces avec vos coordonnées." },
  { icon: Rocket, color: "#F4B400", title: "Boosts prioritaires", desc: "Placez vos annonces en tête des résultats avec des boosts inclus dans l'abonnement." },
  { icon: BarChart3, color: "#11783A", title: "Analytics avancés", desc: "Suivez chaque vue, message et contact reçu par annonce. Optimisez vos performances." },
  { icon: MessageCircle, color: "#D6202C", title: "Messagerie directe", desc: "Chat intégré avec vos prospects, notifications push et emails pour ne rien manquer." },
  { icon: Bell, color: "#F4B400", title: "Alertes clients", desc: "Vos annonces notifiées automatiquement aux visiteurs correspondant à leurs critères." },
];

const HOW_STEPS = [
  { num: "01", title: "Inscription", desc: "Créez votre compte Pro en 2 minutes." },
  { num: "02", title: "Vérification KYC", desc: "Envoyez votre RC et attestation fiscale, notre équipe valide sous 24-48h." },
  { num: "03", title: "Abonnement", desc: "Choisissez votre pays, paiement sécurisé Stripe." },
  { num: "04", title: "Publiez", desc: "Vos annonces avec le badge Pro apparaissent immédiatement." },
];

const FAQ = [
  { q: "Puis-je essayer avant de m'engager ?", a: "Absolument. Créez d'abord un compte gratuit \"Particulier\", publiez quelques annonces pour tester la plateforme. Vous pourrez basculer en compte Pro à tout moment depuis votre dashboard." },
  { q: "Combien de temps prend la vérification KYC ?", a: "En moyenne 24 à 48 heures ouvrées. Notre équipe examine votre Registre de Commerce et votre attestation fiscale pour garantir la fiabilité de la plateforme." },
  { q: "Puis-je annuler mon abonnement ?", a: "Oui, à tout moment sans engagement. L'abonnement est reconductible mensuellement — vous gardez l'accès jusqu'à la fin de la période payée." },
  { q: "Combien d'annonces puis-je publier ?", a: "Les comptes Pro n'ont pas de limite sur le nombre d'annonces actives. Publiez autant que vous voulez." },
  { q: "Le paiement est-il sécurisé ?", a: "Oui, 100 %. Nous utilisons Stripe, leader mondial du paiement en ligne, certifié PCI-DSS. Nous ne stockons jamais vos données bancaires." },
  { q: "Puis-je changer de pays d'abonnement ?", a: "Contactez notre support et nous ajusterons votre abonnement selon votre pays d'activité (MAD, DZD, TND)." },
  { q: "Y a-t-il des frais cachés ?", a: "Aucun. Le tarif affiché est le tarif final. Les boosts inclus dans l'abonnement sont gratuits pour vous. Pas de commission sur les transactions." },
];

const TESTIMONIALS = [
  { name: "Karim B.", agency: "Atlas Immobilier · Casablanca", quote: "Depuis qu'on est passés Pro sur 3aqarate, on reçoit 3 à 5 demandes qualifiées par jour. Le badge de vérification fait toute la différence." },
  { name: "Leila M.", agency: "Sahel Immo · Alger", quote: "L'interface est claire, la messagerie intégrée nous fait gagner un temps fou. Nos clients apprécient de voir nos autres annonces sur notre page agence." },
  { name: "Sami T.", agency: "Carthage Realty · Tunis", quote: "Les boosts fonctionnent vraiment. Une villa que j'avais depuis 2 mois a trouvé preneur en 8 jours après le boost." },
];

export default function ForAgencies() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/packages");
        setPackages(data.pro_subscription);
      } catch {
        /* silent */
      }
    })();
  }, []);

  const startSubscription = async () => {
    if (!user) {
      navigate("/register?role=pro&from=agencies");
      return;
    }
    if (user.role !== "pro") {
      toast.info("Ton compte est en Particulier. Contacte le support pour passer en Pro.");
      return;
    }
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "pro_subscription",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <Layout>
      <SEO
        title="Devenir agence Pro vérifiée · 3aqarate"
        description="Rejoignez la plateforme immobilière n°1 du Maghreb. Badge Pro vérifié, page agence dédiée, boosts inclus. Abonnement mensuel sans engagement."
      />

      {/* HERO */}
      <section className="bg-gradient-to-br from-[#11783A] via-[#0d5e2d] to-[#0a4e25] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(244,180,0,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(214,32,44,0.4) 0%, transparent 40%)",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[11px] uppercase tracking-[0.2em] font-medium mb-6">
              <Sparkles className="w-3 h-3 text-[#F4B400]" /> Pour les professionnels
            </div>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.05] mb-6 tracking-tight" data-testid="pour-agences-hero-title">
              Recrutez 10× plus<br />
              de clients qualifiés<br />
              <span className="text-[#F4B400]">au Maghreb.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-2xl mb-8 leading-relaxed">
              3aqarate est la seule marketplace immobilière panMaghreb. Vitrine dédiée, badge vérifié, boosts inclus,
              analytics avancés. Un abonnement mensuel, zéro commission.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={startSubscription}
                disabled={checkoutLoading}
                data-testid="pour-agences-cta-primary"
                className="rounded-xl h-14 px-8 text-base bg-[#D6202C] hover:bg-[#B5181F] shadow-2xl"
              >
                {checkoutLoading ? "Redirection..." : "Devenir Pro vérifié"} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <a href="#tarifs" className="inline-flex items-center justify-center h-14 px-6 rounded-xl bg-white/15 hover:bg-white/25 backdrop-blur transition-colors text-sm font-medium">
                Voir les tarifs
              </a>
            </div>

            <div className="flex items-center gap-6 mt-10 text-sm text-white/70">
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#F4B400]" /> Sans engagement</div>
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#F4B400]" /> Paiement sécurisé Stripe</div>
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#F4B400]" /> 3 pays supportés</div>
            </div>
          </div>
        </div>
      </section>

      {/* BÉNÉFICES */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.2em] uppercase text-[#D6202C] font-medium">Bénéfices</span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-2 mb-4">
              Tout ce qu'il vous faut pour<br />
              <span className="italic text-[#11783A]">développer votre agence.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pour-agences-benefits">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className="bg-white rounded-2xl border border-[#E5E5E5] p-6 hover:shadow-md transition">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                  style={{ background: `${b.color}15`, color: b.color }}
                >
                  <b.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-xl font-medium mb-2">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TARIFS */}
      <section id="tarifs" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.2em] uppercase text-[#D6202C] font-medium">Tarifs</span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-2 mb-4">
              Un tarif adapté à chaque pays
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Facturé mensuellement dans votre devise locale. Sans engagement, résiliable à tout moment.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto" data-testid="pour-agences-pricing">
            {["MA", "DZ", "TN"].map((country, idx) => {
              const p = packages?.[country];
              const isFeatured = country === "MA";
              return (
                <div
                  key={country}
                  data-testid={`pricing-card-${country}`}
                  className={`relative rounded-3xl p-8 border-2 transition ${
                    isFeatured
                      ? "border-[#D6202C] bg-gradient-to-b from-white to-[#FEF2F3] shadow-xl scale-105"
                      : "border-[#E5E5E5] bg-white hover:border-[#D6202C]/40"
                  }`}
                >
                  {isFeatured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#D6202C] text-white text-[11px] uppercase tracking-wider font-medium">
                      Le + populaire
                    </div>
                  )}
                  <div className="text-3xl mb-2">{COUNTRY_FLAGS[country]}</div>
                  <div className="text-sm text-muted-foreground mb-1">{COUNTRY_LABELS[country]}</div>
                  <div className="font-heading text-5xl font-medium tracking-tight mb-1">
                    {p ? Math.round(p.amount).toLocaleString("fr-FR") : "—"}
                    <span className="text-lg text-muted-foreground ml-1">{p ? (CURRENCY_SYMBOLS[p.currency.toUpperCase()] || p.currency.toUpperCase()) : ""}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mb-6">/ mois</div>

                  <ul className="space-y-3 mb-8 text-sm">
                    {[
                      "Badge Pro vérifié",
                      "Page agence /agence/nom",
                      "Annonces illimitées",
                      "3 boosts inclus / mois",
                      "Analytics avancés",
                      "Messagerie prioritaire",
                      "Support dédié",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#11783A] mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={startSubscription}
                    disabled={checkoutLoading}
                    data-testid={`pricing-cta-${country}`}
                    className={`w-full rounded-xl h-12 ${
                      isFeatured
                        ? "bg-[#D6202C] hover:bg-[#B5181F] text-white"
                        : "bg-[#11783A] hover:bg-[#0d5e2d] text-white"
                    }`}
                  >
                    Choisir cet abonnement
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section className="py-20 md:py-28 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.2em] uppercase text-[#D6202C] font-medium">Comment ça marche</span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-2">
              4 étapes, 48 heures max
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4" data-testid="pour-agences-how">
            {HOW_STEPS.map((s, i) => (
              <div key={s.num} className="bg-white rounded-2xl p-6 border border-[#E5E5E5] relative">
                <div className="text-5xl font-heading font-medium text-[#D6202C]/20 leading-none mb-3">{s.num}</div>
                <h3 className="font-heading text-lg font-medium mb-1">{s.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.2em] uppercase text-[#D6202C] font-medium">Ils nous font confiance</span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-2">
              Ce que disent nos agences
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5" data-testid="pour-agences-testimonials">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-[#FAFAFA] rounded-2xl p-6 border border-[#E5E5E5]">
                <div className="flex gap-0.5 mb-3">
                  {[0,1,2,3,4].map((s) => (
                    <Star key={s} className="w-4 h-4 fill-[#F4B400] text-[#F4B400]" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed mb-4 italic">"{t.quote}"</p>
                <div className="text-sm font-medium">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.agency}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.2em] uppercase text-[#D6202C] font-medium">FAQ</span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-2">
              Questions fréquentes
            </h2>
          </div>
          <div className="space-y-3" data-testid="pour-agences-faq">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`faq-question-${i}`}
                  className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left hover:bg-[#FAFAFA] transition"
                >
                  <span className="font-medium text-sm flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-[#D6202C] shrink-0" />
                    {f.q}
                  </span>
                  <span className={`text-[#D6202C] text-lg transition-transform ${openFaq === i ? "rotate-45" : ""}`}>+</span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed pl-11">
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-[#D6202C] to-[#B5181F] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <TrendingUp className="w-12 h-12 mx-auto mb-4 text-[#F4B400]" />
          <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mb-4">
            Prêt à faire décoller votre agence ?
          </h2>
          <p className="text-white/85 max-w-xl mx-auto mb-8">
            Rejoignez les agences immobilières qui misent sur la vérification et la visibilité pour attirer plus de clients qualifiés.
          </p>
          <Button
            onClick={startSubscription}
            disabled={checkoutLoading}
            data-testid="pour-agences-cta-final"
            className="rounded-xl h-14 px-8 text-base bg-white text-[#D6202C] hover:bg-[#F4B400] hover:text-[#1A1A1A] shadow-2xl transition-colors"
          >
            {checkoutLoading ? "Redirection..." : "Devenir Pro vérifié maintenant"} <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-xs text-white/60 mt-4">Sans engagement · Résiliable à tout moment</p>
        </div>
      </section>
    </Layout>
  );
}
